// Razorpay client-side helper. Lazy-loads checkout.js and wraps the modal flow.
import api from "./api";

let _scriptPromise = null;
const loadScript = () => {
  if (window.Razorpay) return Promise.resolve(true);
  if (_scriptPromise) return _scriptPromise;
  _scriptPromise = new Promise((resolve) => {
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
  return _scriptPromise;
};

/**
 * Pay via Razorpay (or simulate when backend returns a mocked order).
 * @param opts.endpoint  Backend order endpoint, e.g. "/payments/order"
 * @param opts.verifyEndpoint Verify endpoint, default "/payments/verify"
 * @param opts.amount    Rupee amount
 * @param opts.purpose   "otp_wallet" | "subscription" | "customer_topup"
 * @param opts.name      Business name shown in checkout
 * @param opts.user      { name, phone }
 * @returns Promise<{captured, payment_id, mocked}>
 */
export async function payViaRazorpay({
  endpoint = "/payments/order",
  verifyEndpoint = "/payments/verify",
  amount, purpose, name = "Financial Advisor", user = {},
}) {
  // 1. Create order
  const { data: order } = await api.post(endpoint, { amount, purpose });
  // 2. If mocked (no Razorpay keys configured), simulate success
  if (order.mocked) {
    const { data } = await api.post(verifyEndpoint, {
      razorpay_order_id: order.order_id,
      razorpay_payment_id: `pay_mock_${Date.now()}`,
      razorpay_signature: "mock_sig",
      purpose,
    });
    return { captured: true, mocked: true, ...data };
  }
  // 3. Real flow
  const ok = await loadScript();
  if (!ok) throw new Error("Razorpay SDK failed to load. Check connectivity.");

  return new Promise((resolve, reject) => {
    const rzp = new window.Razorpay({
      key: order.key_id,
      amount: order.amount,
      currency: order.currency,
      name,
      description: purpose,
      order_id: order.order_id,
      handler: async (response) => {
        try {
          const { data } = await api.post(verifyEndpoint, {
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature,
            purpose,
          });
          resolve({ captured: true, mocked: false, ...data });
        } catch (e) { reject(e); }
      },
      prefill: {
        name: user.name || "",
        contact: user.phone || "",
      },
      theme: { color: "#0F172A" },
      modal: { ondismiss: () => reject(new Error("Payment cancelled")) },
    });
    rzp.open();
  });
}
